import { useState } from 'react'

import useTranslations from '../../../hooks/useTranslations'
import ANTDButton from '../../../shared/antd/ANTDButton'
import ANTDModal from '../../../shared/antd/ANTDModal'
import ANTDTable from '../../../shared/antd/ANTDTable'
import { dateFormat } from '../../../utils/dateFormat'
import { isEqual } from '../../../utils/javascript'
import { priorityOptions } from '../user.description'

const convertHtmlToText = html => {
  if (!html) return '-'

  const normalizedHtml = String(html)
    .replace(/<\/p>\s*<p>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '')
    .replace(/<p>/gi, '')

  if (typeof window === 'undefined') {
    return normalizedHtml.replace(/<[^>]+>/g, '').trim() || '-'
  }

  const parser = document.createElement('textarea')
  parser.innerHTML = normalizedHtml.replace(/<[^>]+>/g, '')
  return parser.value.trim() || '-'
}

const PreviousRemindersModal = ({
  open,
  userDetails,
  reminderData,
  onCancel,
  onResolve,
  resolvingId,
  onTableChange,
}) => {
  const { t } = useTranslations()
  const [selectedReminder, setSelectedReminder] = useState(null)

  const handleViewReminder = reminder => {
    setSelectedReminder(reminder)
  }

  const handleCloseViewReminder = () => {
    setSelectedReminder(null)
  }

  const reminderDetails = [
    {
      label: t('user_ID'),
      value: selectedReminder?.id || '-',
    },
    {
      label: t('issue_Subject'),
      value: selectedReminder?.subject || '-',
    },
    {
      label: t('user_reminderDeadlineDate'),
      value: selectedReminder?.deadLineDate || '-',
    },
    {
      label: t('issue_Priority'),
      value:
        priorityOptions(t)?.filter(v =>
          isEqual(v?.value, selectedReminder?.priority),
        )?.[0]?.label || '-',
    },
    {
      label: t('inv_Body'),
      value: convertHtmlToText(selectedReminder?.body),
    },
  ]

  const columns = [
    {
      title: t('user_ID'),
      dataIndex: 'id',
      key: 'id',
      render: value => value || '-',
    },
    {
      title: t('issue_Subject'),
      dataIndex: 'subject',
      key: 'subject',
      render: value => value || '-',
    },
    {
      title: t('user_reminderDeadlineDate'),
      dataIndex: 'deadLineDate',
      key: 'deadline',
      render: (_, rowData) => rowData?.deadLineDate || '-',
    },
    {
      title: t('issue_Priority'),
      dataIndex: 'priority',
      key: 'priority',
      render: value =>
        priorityOptions(t)?.filter(v =>
          isEqual(v?.value, selectedReminder?.priority),
        )?.[0]?.label ||
        value ||
        '-',
    },
    {
      title: t('user_reminderIsResolved'),
      dataIndex: 'isResolved',
      key: 'isResolved',
      render: value => t(value ? 'txt_Yes' : 'txt_No'),
    },
    {
      title: t('user_CreationDate'),
      dataIndex: 'creationDate',
      key: 'creationDate',
      render: value => (value ? dateFormat(value)?.newDate : '-'),
    },
    {
      title: t('txt_Action'),
      key: 'action',
      render: (_, rowData) => (
        <div className="d-flex flex-nowrap">
          <ANTDButton
            type="primary"
            className="btn"
            onClick={() => handleViewReminder(rowData)}
          >
            {t('btn_View')}
          </ANTDButton>
          {!rowData?.isResolved && (
            <ANTDButton
              type="primary"
              className="btn ml-5"
              loading={resolvingId === rowData?.id}
              onClick={() => onResolve?.(rowData)}
            >
              {t('btn_Resolve')}
            </ANTDButton>
          )}
        </div>
      ),
    },
  ]

  const {
    list = [],
    loader = false,
    pageNo = 1,
    total = 0,
  } = reminderData || {}

  return (
    <ANTDModal
      title={t('user_previousReminders')}
      centered
      open={open}
      onCancel={onCancel}
      footer={false}
      width={1100}
    >
      <div className="mb-10"></div>
      <ANTDTable
        dataSource={list?.map(item => ({ ...item, key: item?.id }))}
        columns={columns}
        loading={loader}
        onChange={onTableChange}
        pagination={{
          lastFetched: pageNo,
          current: pageNo,
          pageSize: 10,
          total,
          responsive: true,
          hideOnSinglePage: true,
        }}
        scroll={{ x: 1000 }}
      />
      <ANTDModal
        title={t('user_previousReminderDetailsTitle')}
        centered
        open={Boolean(selectedReminder)}
        onCancel={handleCloseViewReminder}
        footer={false}
        width={760}
      >
        <div>
          {reminderDetails.map(({ label, value }) => (
            <div
              key={label}
              style={{ marginBottom: '12px', whiteSpace: 'pre-wrap' }}
            >
              <strong>{label}: </strong>
              <span>{value}</span>
            </div>
          ))}
        </div>
      </ANTDModal>
    </ANTDModal>
  )
}

export default PreviousRemindersModal
